import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../api/client';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  // Mode: 'login' | 'register'
  const [mode, setMode] = useState<'login' | 'register'>('login');
  // Login method: 'password' | 'token'
  const [loginMethod, setLoginMethod] = useState<'password' | 'token'>('password');

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tokenInput, setTokenInput] = useState('');

  // Register form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');

  // Post-register recovery key modal
  const [registeredToken, setRegisteredToken] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (loginMethod === 'password') {
        if (!email.trim() || !password) {
          throw new Error('Vui lòng nhập đầy đủ Email và Mật khẩu.');
        }
        await login({ email: email.trim(), password });
      } else {
        if (!tokenInput.trim()) {
          throw new Error('Vui lòng nhập Khóa đăng nhập (API Token).');
        }
        await login({ token: tokenInput.trim() });
      }
      navigate('/chat', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đăng nhập thất bại.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!regName.trim()) {
      setError('Vui lòng nhập Họ tên hoặc Tên tổ chức.');
      return;
    }
    if (!regEmail.trim()) {
      setError('Vui lòng nhập địa chỉ Email.');
      return;
    }
    if (regPassword.length < 6) {
      setError('Mật khẩu phải có tối thiểu 6 ký tự.');
      return;
    }
    if (regPassword !== regConfirmPassword) {
      setError('Mật khẩu xác nhận không trùng khớp.');
      return;
    }

    setLoading(true);
    try {
      const data = await api.register({
        name: regName.trim(),
        email: regEmail.trim(),
        password: regPassword
      });
      setRegisteredToken(data.token);
      setTokenInput(data.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Đăng ký thất bại.');
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    if (registeredToken) {
      navigator.clipboard.writeText(registeredToken);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };

  const proceedWithRegisteredToken = async () => {
    if (!registeredToken) return;
    setLoading(true);
    try {
      await login({ token: registeredToken });
      navigate('/chat', { replace: true });
    } catch {
      setRegisteredToken(null);
      setMode('login');
      setLoginMethod('password');
      setEmail(regEmail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: '440px', width: '100%' }}>
        <div className="login-card__logo">
          <div className="login-card__logo-icon">📄</div>
          <span className="login-card__logo-text">ErixOne</span>
        </div>

        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '24px' }}>
          Document Intelligence as a Service (DIaaS)
        </p>

        {/* Top-level Mode Switcher: Sign In vs Register */}
        {!registeredToken && (
          <div className="tabs" style={{ margin: '0 auto 20px', display: 'flex' }}>
            <button
              className={`tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => { setMode('login'); setError(''); }}
            >
              Đăng nhập
            </button>
            <button
              className={`tab ${mode === 'register' ? 'active' : ''}`}
              onClick={() => { setMode('register'); setError(''); }}
            >
              Đăng ký mới
            </button>
          </div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
            fontSize: '13px', color: '#f87171'
          }}>
            {error}
          </div>
        )}

        {/* Case 1: Post-Registration Recovery Key Showcase */}
        {registeredToken ? (
          <div>
            <div style={{
              background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: '12px', padding: '18px', marginBottom: '20px',
              fontSize: '13px'
            }}>
              <div style={{ fontWeight: 600, color: '#34d399', fontSize: '15px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🎉</span> Đăng ký tài khoản thành công!
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '12.5px', lineHeight: '1.5', margin: '0 0 14px 0' }}>
                Bạn có thể dùng <strong>{regEmail}</strong> và mật khẩu vừa tạo để đăng nhập hằng ngày.
              </p>

              <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '12px', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                  🔑 Khóa khẩn cấp & API Token:
                </div>
                <div style={{
                  fontFamily: 'var(--font-mono)', color: '#a78bfa', fontSize: '12px',
                  wordBreak: 'break-all', userSelect: 'all', marginBottom: '10px',
                  background: 'rgba(0,0,0,0.25)', padding: '8px 10px', borderRadius: '6px'
                }}>
                  {registeredToken}
                </div>
                <button
                  type="button"
                  onClick={copyToken}
                  className="btn btn--secondary btn--sm w-full"
                  style={{ justifyContent: 'center' }}
                >
                  {copiedToken ? '✓ Đã sao chép khóa' : '📋 Sao chép khóa khẩn cấp'}
                </button>
              </div>

              <div style={{ fontSize: '11.5px', color: 'var(--accent-amber)', marginTop: '12px', lineHeight: '1.4' }}>
                ⚠️ <strong>Lưu ý quan trọng:</strong> Hãy cất giữ khóa này cẩn thận. Bạn có thể dùng khóa này để đăng nhập khi quên mật khẩu hoặc cấp quyền kết nối cho AI Agent (EricSpace, Cursor, Claude...).
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={proceedWithRegisteredToken}
                className="btn btn--primary w-full"
                disabled={loading}
                style={{ justifyContent: 'center', padding: '11px', fontSize: '13.5px' }}
              >
                {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Vào Bảng Điều Khiển ngay →'}
              </button>
              <button
                onClick={() => {
                  setRegisteredToken(null);
                  setMode('login');
                  setLoginMethod('password');
                  setEmail(regEmail);
                }}
                className="btn btn--ghost w-full"
                style={{ justifyContent: 'center', fontSize: '12.5px', color: 'var(--text-secondary)' }}
              >
                Về màn hình đăng nhập
              </button>
            </div>
          </div>
        ) : mode === 'login' ? (
          /* Case 2: Login Mode (Dual Tabs: Account vs Recovery Token) */
          <div>
            {/* Sub-tabs: Password vs Token */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px',
              background: 'var(--bg-elevated)', padding: '4px', borderRadius: '8px',
              marginBottom: '18px', border: '1px solid var(--border)'
            }}>
              <button
                type="button"
                onClick={() => { setLoginMethod('password'); setError(''); }}
                style={{
                  border: 'none', background: loginMethod === 'password' ? 'var(--bg-surface)' : 'transparent',
                  color: loginMethod === 'password' ? 'var(--text-primary)' : 'var(--text-muted)',
                  borderRadius: '6px', padding: '7px 10px', fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '6px',
                  boxShadow: loginMethod === 'password' ? '0 1px 3px rgba(0,0,0,0.3)' : 'none'
                }}
              >
                <span>👤</span> Tài khoản
              </button>
              <button
                type="button"
                onClick={() => { setLoginMethod('token'); setError(''); }}
                style={{
                  border: 'none', background: loginMethod === 'token' ? 'var(--bg-surface)' : 'transparent',
                  color: loginMethod === 'token' ? 'var(--text-primary)' : 'var(--text-muted)',
                  borderRadius: '6px', padding: '7px 10px', fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '6px',
                  boxShadow: loginMethod === 'token' ? '0 1px 3px rgba(0,0,0,0.3)' : 'none'
                }}
              >
                <span>🔑</span> Khóa dự phòng
              </button>
            </div>

            <form onSubmit={handleLogin}>
              {loginMethod === 'password' ? (
                <>
                  <div className="form-group">
                    <label className="label">Địa chỉ Email</label>
                    <input
                      className="input"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="name@company.com"
                      required
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label className="label">Mật khẩu</label>
                    <input
                      className="input"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label className="label" style={{ margin: 0 }}>Khóa khẩn cấp / API Bearer Token</label>
                  </div>
                  <input
                    className="input"
                    type="password"
                    value={tokenInput}
                    onChange={e => setTokenInput(e.target.value)}
                    placeholder="sk-one-..."
                    required
                    autoFocus
                  />
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: '1.4' }}>
                    💡 Dùng chuỗi khóa <code style={{ fontFamily: 'var(--font-mono)', color: '#a78bfa' }}>sk-one-...</code> được cấp để đăng nhập khẩn cấp khi quên mật khẩu hoặc môi trường dev.
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="btn btn--primary w-full"
                disabled={loading}
                style={{ justifyContent: 'center', padding: '10px', marginTop: '6px' }}
              >
                {loading ? (
                  <span className="spinner" style={{ width: 16, height: 16 }} />
                ) : loginMethod === 'password' ? (
                  'Đăng nhập'
                ) : (
                  'Xác thực với Khóa dự phòng'
                )}
              </button>
            </form>
          </div>
        ) : (
          /* Case 3: Registration Form */
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label className="label">Họ tên hoặc Tên tổ chức</label>
              <input
                className="input"
                type="text"
                value={regName}
                onChange={e => setRegName(e.target.value)}
                placeholder="Ví dụ: Nguyễn Văn A hoặc Công ty ABC"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="label">Địa chỉ Email</label>
              <input
                className="input"
                type="email"
                value={regEmail}
                onChange={e => setRegEmail(e.target.value)}
                placeholder="name@company.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="label">Mật khẩu</label>
              <input
                className="input"
                type="password"
                value={regPassword}
                onChange={e => setRegPassword(e.target.value)}
                placeholder="Tối thiểu 6 ký tự"
                required
              />
            </div>

            <div className="form-group">
              <label className="label">Xác nhận Mật khẩu</label>
              <input
                className="input"
                type="password"
                value={regConfirmPassword}
                onChange={e => setRegConfirmPassword(e.target.value)}
                placeholder="Nhập lại mật khẩu"
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn--primary w-full"
              disabled={loading}
              style={{ justifyContent: 'center', padding: '10px', marginTop: '6px' }}
            >
              {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Tạo tài khoản & Nhận Khóa'}
            </button>
          </form>
        )}

        <div style={{ marginTop: '24px', paddingTop: '18px', borderTop: '1px solid var(--border)' }}>
          <p style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
            Domain: <span style={{ color: 'var(--accent-cyan)' }}>one.wowerix.com</span><br />
            DIaaS Engine Core — Ecosystem Native
          </p>
        </div>
      </div>
    </div>
  );
}
